import React, { useState } from "react";
import cropr from "../shared/utils/cropr";
import { interpolate } from "../shared/utils/legacy";
import FileUploadModal from "./FileUploadModal";
import FileOverviewCards from "./FileOverviewCards";
import FileOverviewTable from "./FileOverviewTable";
import gettext from "../shared/utils/gettext";
import { LinkFieldProps, LinksType } from "./LinkedFields";
import { PermissionObject } from "./utils";
import cx from "classnames";
import Icon from "../shared/icons/Icon";

export interface FileOverviewProps {
  attachments: any[];
  permission: PermissionObject;
  onChange?: any;
  editable?: boolean;
  destroyable?: boolean;
  showAttachedTo?: boolean;
}
const FileOverview = ({
  attachments,
  permission,
  onChange = () => {},
  editable = true,
  destroyable = false,
  showAttachedTo,
}: FileOverviewProps) => {
  const [overviewVariant, setOverviewVariant] = useState<string>(() => {
    return localStorage.getItem("FileOverviewViewMode") || "cards";
  });
  const [showModal, setShowModal] = useState(false);
  const pageSize = 12;
  const [page, setPage] = useState<number>(1);
  const [attachmentList, setAttachmentList] = useState<any[]>(() => {
    const allAttachments = cropr.documents.models
      .concat(cropr.images.models)
      .sort((a, b) => (a.get("last_modified") > b.get("last_modified") ? 1 : -1));
    return allAttachments.filter(
      (attachment) =>
        attachments && attachments.map((a: any) => a.key).includes(attachment.get("key"))
    );
  });

  const getCurrentPageAttachments = () =>
    attachmentList.slice((page - 1) * pageSize, (page - 1) * pageSize + pageSize); //slice is 0 indexed

  const changeOverviewVariant = (state: string) => {
    setOverviewVariant(state);
    localStorage.setItem("FileOverviewViewMode", state);
  };

  const removeAttachment = (attachmentKey: string) => {
    let filteredAttachmentList = attachmentList.filter((item) => item.get("key") !== attachmentKey);
    setAttachmentList(filteredAttachmentList);
    onChange(filteredAttachmentList.map((attachment) => attachment.get("key")));
  };
  const destroyAttachment = (attachmentKey: string) => {
    let imgObj: any = cropr.images.findWhere({ key: attachmentKey });
    const linksObj: LinkFieldProps = imgObj?.getLinks();
    const usedCount = linksObj
      ? (Object.values(linksObj) as Array<LinksType[]>).reduce((accumulator, linksTypeArray) => {
          return accumulator + linksTypeArray.length;
        }, 0)
      : 0;
    let msg = gettext("Are you sure? This image will be removed permanently!");
    if (usedCount)
      msg = interpolate(
        gettext("%s It is still used in %s records/cropfields/parcels/machines/avatars."),
        [msg, usedCount]
      );
    if (cropr.confirm(msg)) {
      imgObj?.destroy({
        wait: true,
        success: () => {
          cropr.notify("success", gettext("Removed image"));
          removeAttachment(attachmentKey);
        },
      });
    }
  };
  const destroyAllAttachments = () => {
    if (
      cropr.confirm(
        gettext("Are you sure? All your images will be removed permanently! This cannot be undone!")
      )
    ) {
      //later add bulk-destroy endpoint
      const imageKeys = cropr.images.map((i) => i.get("key"));
      imageKeys.forEach((attachmentKey) => {
        let imgObj: any = cropr.images.findWhere({ key: attachmentKey });
        imgObj?.destroy({
          wait: true,
          success: () => {
            cropr.notify("success", gettext("Removed image"));
          },
        });
      });
      setAttachmentList([]); //ideally we should update only if we are sure that it was really deleted
      onChange([]);
    }
  };
  const addAttachments = (selected: string[]) => {
    const selectedObjects = cropr.documents.models
      .concat(cropr.images.models)
      .filter((o) => selected.includes(o.get("key")));
    let newAttachmentList = attachmentList.concat(selectedObjects);
    setAttachmentList(newAttachmentList);
    onChange(newAttachmentList.map((attachment) => attachment.get("key")));
  };

  return (
    <>
      <div className="row">
        <div className="controls col">
          {cropr.features.photo &&
            editable &&
            permission.create &&
            permission.update && ( //maybe show 'BUY PRO' instead?
              <>
                <button
                  type="button"
                  className="btn btn-outline-green upload-btn"
                  onClick={() => setShowModal(true)}
                >
                  <Icon icon={"add-plus"} className={"pr-2"} />
                  {gettext("Attachment")}
                </button>
                <FileUploadModal
                  attachments={attachments}
                  show={showModal}
                  handleClose={() => setShowModal(false)}
                  onChange={addAttachments}
                />
              </>
            )}
        </div>
        <div className="col text-center">
          {attachmentList.length > 0 && (
            <h4>
              {gettext("Attached files")}:{" "}
              {attachmentList.length ? interpolate("%s", [attachmentList.length]) : ""}
            </h4>
          )}
        </div>
        <div className="col">
          <div className="btn-group float-right" role="group">
            {attachmentList.length > 0 && destroyable && permission.delete && (
              <button
                type="button"
                className="btn text-danger"
                onClick={() => destroyAllAttachments()}
              >
                {gettext("Delete all")}
              </button>
            )}
            <button
              type="button"
              className={cx("btn", overviewVariant === "cards" ? "btn-primary" : "btn-smoke")}
              onClick={() => {
                changeOverviewVariant("cards");
              }}
            >
              <Icon icon={"table"} />
            </button>
            <button
              type="button"
              className={cx("btn", overviewVariant === "table" ? "btn-primary" : "btn-smoke")}
              onClick={() => {
                changeOverviewVariant("table");
              }}
            >
              <Icon icon={"list"} />
            </button>
          </div>
        </div>
      </div>
      <div className="img row">
        {attachmentList.length && permission.read ? (
          <>
            <div className="col-12">
              {overviewVariant === "cards" ? (
                <FileOverviewCards
                  attachmentList={getCurrentPageAttachments()}
                  editable={editable && permission.update}
                  onClickRemove={removeAttachment}
                  destroyable={destroyable && permission.delete}
                  onClickDestroy={destroyAttachment}
                />
              ) : (
                <FileOverviewTable
                  attachmentList={getCurrentPageAttachments()}
                  editable={editable && permission.update}
                  onClickRemove={removeAttachment}
                  destroyable={destroyable && permission.delete}
                  onClickDestroy={destroyAttachment}
                  showAttachedTo={!!showAttachedTo}
                />
              )}
            </div>
          </>
        ) : (
          <div className="col">
            <span>{gettext("No attachments")}</span>
          </div>
        )}
      </div>
      {attachmentList.length > 0 && permission.read && (
        <div className="row">
          <div className="">
            <button
              type="button"
              disabled={page <= 1}
              className="btn btn-outline-smoke float-left"
              onClick={() => setPage(1)}
            >
              {gettext("First page")}
            </button>
          </div>
          <div className="ml-1">
            <button
              type="button"
              disabled={page <= 1}
              className="btn btn-outline-smoke float-left"
              onClick={() => setPage(page - 1)}
            >
              {gettext("Previous page")}
            </button>
          </div>
          <div className="col my-auto text-center">
            {gettext("Page")} {page}/{Math.ceil(attachmentList.length / pageSize)}
          </div>
          <div className="mr-1">
            <button
              type="button"
              disabled={page >= attachmentList.length / pageSize}
              className="btn btn-outline-smoke float-right"
              onClick={() => setPage(page + 1)}
            >
              {gettext("Next page")}
            </button>
          </div>
          <div className="">
            <button
              type="button"
              disabled={page >= attachmentList.length / pageSize}
              className="btn btn-outline-smoke float-right"
              onClick={() => setPage(Math.ceil(attachmentList.length / pageSize))}
            >
              {gettext("Last page")}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default FileOverview;
